/**
 * Channel Attribution — placeholder dataset + pure aggregation logic.
 *
 * Mirrors the standalone Favor reporting dashboard one-to-one, but as plain JS
 * the React view can import. Replace the placeholder `RAW`/`TIERS`/`RATIOS`
 * with live backend output (GET /admin/attribution/members-by-channel) — the
 * tree auto-aggregates, so the math below never changes.
 *
 * Account statuses are the real Favor states (names in utils/memberStatus.js). Every
 * sign up is in exactly one status:
 *   signups = approved + hidden + inactive + ghost + deactivated + suspended + deleted
 * `active` is NOT a status — it is the total approved + hidden, carried on every block
 * (the server sends it too) and never added into signups.
 * (subscribed is an OVERLAY across every bucket — the cohort's members who ever paid,
 * whatever status they hold now — not a subset of active and not a bucket of its own).
 */
import { STATUS_ORDER, activeTotal } from '../../utils/memberStatus';

export const RAW = [
  { name: 'Meta', cls: 'd-meta', children: [
    { name: 'Campaign · Sign ups – Women', children: [
      { name: 'Ad group · Women', children: [
        { name: 'Video Set 9', leaf: { f: { signups: 168, active: 110, approved: 54, hidden: 56, inactive: 6, ghost: 30, deactivated: 8, deleted: 14, subscribed: 4, spend: 265000 } } },
        { name: 'IMG 3', leaf: { f: { signups: 147, active: 109, approved: 60, hidden: 49, inactive: 6, ghost: 18, deactivated: 6, deleted: 8, subscribed: 7, spend: 211000 } } },
        { name: 'IMG 1', leaf: { f: { signups: 110, active: 80, approved: 46, hidden: 34, inactive: 4, ghost: 16, deactivated: 5, deleted: 5, subscribed: 5, spend: 153000 } } },
        { name: 'Video Set -1', leaf: { f: { signups: 55, active: 40, approved: 19, hidden: 21, inactive: 2, ghost: 7, deactivated: 3, deleted: 3, subscribed: 2, spend: 77000 } } },
        { name: 'Video Set 11', leaf: { f: { signups: 60, active: 46, approved: 30, hidden: 16, inactive: 2, ghost: 6, deactivated: 3, deleted: 3, subscribed: 5, spend: 81000 } } },
        { name: 'IMG 4', leaf: { f: { signups: 40, active: 30, approved: 18, hidden: 12, inactive: 1, ghost: 5, deactivated: 2, deleted: 2, subscribed: 3, spend: 54000 } } },
      ] },
    ] },
    { name: 'Campaign · Sign ups – Men', children: [
      { name: 'Ad group · Men', children: [
        { name: 'Carousel M1', leaf: { m: { signups: 520, active: 335, approved: 150, hidden: 185, inactive: 20, ghost: 110, deactivated: 25, deleted: 30, subscribed: 9, spend: 120000 } } },
        { name: 'Static M2', leaf: { m: { signups: 360, active: 253, approved: 120, hidden: 133, inactive: 15, ghost: 62, deactivated: 18, deleted: 12, subscribed: 11, spend: 88000 } } },
        { name: 'Video M3', leaf: { m: { signups: 300, active: 209, approved: 96, hidden: 113, inactive: 13, ghost: 50, deactivated: 15, deleted: 13, subscribed: 8, spend: 74000 } } },
      ] },
    ] },
  ] },
  { name: 'Google', cls: 'd-google', children: [
    { name: 'Campaign · Search – Brand', children: [
      { name: 'Ad group · Brand terms', children: [
        { name: 'RSA — Elite dating', leaf: { f: { signups: 120, active: 94, approved: 58, hidden: 36, inactive: 4, ghost: 12, deactivated: 4, deleted: 6, subscribed: 9, spend: 42000 }, m: { signups: 160, active: 123, approved: 80, hidden: 43, inactive: 5, ghost: 20, deactivated: 6, deleted: 6, subscribed: 14, spend: 38000 } } },
      ] },
    ] },
    { name: 'Campaign · Discovery', children: [
      { name: 'Ad group · Lifestyle', children: [
        { name: 'Image — Luxe', leaf: { f: { signups: 90, active: 64, approved: 34, hidden: 30, inactive: 3, ghost: 14, deactivated: 4, deleted: 5, subscribed: 4, spend: 34000 }, m: { signups: 120, active: 82, approved: 48, hidden: 34, inactive: 4, ghost: 22, deactivated: 6, deleted: 6, subscribed: 6, spend: 30000 } } },
      ] },
    ] },
  ] },
  { name: 'Reddit', cls: 'd-reddit', children: [
    { name: 'Campaign · Male niche', children: [
      { name: 'Ad group · r/Delhi + interest subs', children: [
        { name: 'Promoted — Discreet', leaf: { m: { signups: 130, active: 97, approved: 58, hidden: 39, inactive: 4, ghost: 18, deactivated: 5, deleted: 6, subscribed: 9, spend: 24000 } } },
        { name: 'Promoted — Status', leaf: { m: { signups: 90, active: 67, approved: 40, hidden: 27, inactive: 3, ghost: 12, deactivated: 4, deleted: 4, subscribed: 6, spend: 14000 } } },
      ] },
    ] },
    { name: 'Campaign · Female interest', children: [
      { name: 'Ad group · Lifestyle subs', children: [
        { name: 'Promoted — Control', leaf: { f: { signups: 70, active: 56, approved: 34, hidden: 22, inactive: 2, ghost: 6, deactivated: 2, deleted: 4, subscribed: 7, spend: 18000 } } },
      ] },
    ] },
  ] },
  { name: 'Organic / Unknown', cls: 'd-organic', leaf: {
    f: { signups: 230, active: 168, approved: 42, hidden: 126, inactive: 14, ghost: 30, deactivated: 8, deleted: 10, subscribed: 3, spend: 0 },
    m: { signups: 480, active: 286, approved: 70, hidden: 216, inactive: 24, ghost: 120, deactivated: 30, deleted: 20, subscribed: 5, spend: 0 },
  } },
];

// Raw tier counts split by gender (the live shape buildTiers now returns); the
// view slices to the selected gender and computes the %/read client-side.
export const TIERS = [
  { key: 'meta', name: 'Meta', cls: 'd-meta', f: { t1: 38, t2: 34, t3: 28 }, m: { t1: 30, t2: 36, t3: 34 } },
  { key: 'google', name: 'Google', cls: 'd-google', f: { t1: 61, t2: 27, t3: 12 }, m: { t1: 55, t2: 30, t3: 15 } },
  { key: 'reddit', name: 'Reddit', cls: 'd-reddit', f: { t1: 69, t2: 22, t3: 9 }, m: { t1: 64, t2: 25, t3: 11 } },
];

export const RATIOS = [
  { when: 'Last review', val: '~4:1', cls: 'accent' },
  { when: 'Today', val: '~3:1', cls: 'warn' },
  { when: 'New sign ups', val: '~1:1', cls: 'good' },
];

export const RANGE_FACTOR = { yesterday: 0.16, '7d': 1, '30d': 3.7, campaign: 5.3 };

/* Platform split — FALLBACK ONLY. Live leaves carry a real per-platform split in
   `leaf._plat[gender][app|web]` from the backend (see platBlock). This heuristic
   is used only for the placeholder dataset / any legacy leaf that has no `_plat`,
   so the sample view still renders an app/web breakdown when the live source is
   down. iOS-web is a minority and converts to pay less. */
const PLATFORM_SPLIT = { webShare: 0.22, skew: { active: 0.9, approved: 0.9, hidden: 1.2, inactive: 1.2, ghost: 1.35, deactivated: 1.0, suspended: 1.0, deleted: 1.15, subscribed: 0.55 } };

// Order matters: a platform row's id ends in its index here, and the drill-through
// reads it back (index.js). 'ios' is the iOS app, live since 2026-10-22; 'web' is the
// website on any device, which until then was where every iOS member came from.
export const PLATFORMS = [['app', 'Android app', 'd-app'], ['ios', 'iOS app', 'd-ios'], ['web', 'Web', 'd-web']];

/* ---- organic grouping (DISABLED) ----
   The backend's organic-family split (organic_play / organic_search / unknown) was
   reverted, so Organic is once again a SINGLE top-level channel. This helper used to
   collapse every non-paid channel under one synthetic "Organic / Unknown" parent to
   undo that split client-side — but with the split gone it wrongly nested the
   genuinely separate channels (Influencer, Pinterest, Email, chatgpt.com, …) AND the
   real organic node under that umbrella. It's now a pass-through: every channel the
   backend returns renders flat at the top level, exactly as before the split. */
export function groupOrganic(tree) {
  return tree;
}

/* ---- formatters ---- */
export const inr = (n) => '₹' + Number(Math.round(n)).toLocaleString('en-IN');
export const inrK = (n) => (n >= 100000 ? '₹' + (n / 100000).toFixed(2) + 'L' : '₹' + Math.round(n / 1000) + 'K');
export const num = (n) => Number(Math.round(n)).toLocaleString('en-IN');
export const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

export const fmtDate = (s) => {
  if (!s) return '';
  const mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const p = s.split('-');
  return parseInt(p[2], 10) + ' ' + mo[parseInt(p[1], 10) - 1] + ' ' + p[0];
};

export function customDays(from, to) {
  if (!from || !to) return 7;
  const d = Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
  return d < 1 ? 1 : d;
}

export function getFactor(range, from, to) {
  if (range === 'custom') return Math.max(customDays(from, to) / 7, 0.05);
  return RANGE_FACTOR[range];
}

/* ---- aggregation ---- */
function splitBlock(b, plat) {
  if (plat === 'all' || !plat) return b;
  // The heuristic only knows the web share; it has nothing to say about the iOS app.
  if (plat === 'ios') return null;
  const wf = PLATFORM_SPLIT.webShare;
  const sk = PLATFORM_SPLIT.skew;
  const webOf = (v, m) => Math.round(v * Math.min(wf * (sk[m] || 1), 0.95));
  const st = STATUS_ORDER;
  const out = {};
  st.forEach((s) => { const w = webOf(b[s] || 0, s); out[s] = plat === 'web' ? w : (b[s] || 0) - w; });
  out.active = out.approved + out.hidden;
  const sw = webOf(b.subscribed, 'subscribed');
  out.subscribed = plat === 'web' ? sw : b.subscribed - sw;
  const cw = webOf(b.completed || 0, 'active');
  out.completed = plat === 'web' ? cw : (b.completed || 0) - cw;
  const fw = webOf(b.filled || 0, 'active');
  out.filled = plat === 'web' ? fw : (b.filled || 0) - fw;
  out.signups = st.reduce((n, s) => n + out[s], 0);
  const totalSign = st.reduce((n, s) => n + (b[s] || 0), 0);
  out.spend = totalSign ? b.spend * (out.signups / totalSign) : 0;
  return out;
}

export function blank() {
  return { signups: 0, active: 0, approved: 0, hidden: 0, inactive: 0, ghost: 0, deactivated: 0, suspended: 0, deleted: 0, subscribed: 0, completed: 0, filled: 0, spend: 0 };
}

function addScaled(z, b, f) {
  if (!b) return;
  // `active` = approved + hidden. Take the block's own total when it has one (server and
  // sample both send it); approved/inactive are guarded so an older server build without
  // them reads 0 instead of NaN.
  z.signups += b.signups * f; z.active += (b.active != null ? b.active : activeTotal(b)) * f;
  z.approved += (b.approved || 0) * f; z.inactive += (b.inactive || 0) * f; z.ghost += b.ghost * f; z.hidden += b.hidden * f;
  z.deactivated += b.deactivated * f; z.suspended += (b.suspended || 0) * f; z.deleted += b.deleted * f; z.subscribed += b.subscribed * f;
  // `completed` (signup-form flag) and `filled` (profileCompletion === 100) are both
  // orthogonal to the buckets and absent from placeholder leaves — guard for them.
  z.completed += (b.completed || 0) * f; z.filled += (b.filled || 0) * f; z.spend += b.spend * f;
}

// One platform's block for a gender. When the backend provided a real split
// (`leaf._plat`), use it verbatim — a MISSING platform key there means zero real
// users, NOT a cue to fabricate via the heuristic (otherwise an app-only channel
// shows a phantom "iOS web" row that the drill-through can't match). The heuristic
// applies only to placeholder/legacy leaves that carry no `_plat` at all.
function platBlock(leaf, gKey, plat) {
  if (leaf._plat) {
    const g = leaf._plat[gKey];
    return (g && g[plat]) ? g[plat] : null;
  }
  const base = leaf[gKey];
  return base ? splitBlock(base, plat) : null;
}

function leafAgg(leaf, mode, f, plat) {
  const z = blank();
  const genders = mode === 'all' ? ['f', 'm'] : [mode];
  for (const g of genders) {
    const blk = (!plat || plat === 'all') ? leaf[g] : platBlock(leaf, g, plat);
    addScaled(z, blk, f);
  }
  return z;
}

function merge(z, o) {
  z.signups += o.signups; z.active += o.active; z.approved += (o.approved || 0); z.inactive += (o.inactive || 0);
  z.ghost += o.ghost; z.hidden += o.hidden; z.deactivated += o.deactivated;
  z.suspended += (o.suspended || 0); z.deleted += o.deleted; z.subscribed += o.subscribed;
  z.completed += (o.completed || 0); z.filled += (o.filled || 0); z.spend += o.spend; return z;
}

export function nodeMetrics(node, mode, f, plat) {
  if (node.leaf) return leafAgg(node.leaf, mode, f, plat);
  const z = blank();
  (node.children || []).forEach((c) => merge(z, nodeMetrics(c, mode, f, plat)));
  return z;
}

export function totals(tree, mode, f, plat) {
  const T = blank();
  tree.forEach((c) => merge(T, nodeMetrics(c, mode, f, plat)));
  return T;
}

/* Flattened drill-down rows: channel › campaign › ad group › creative › platform.
   `role` is the attribution ROLE (independent of raw depth) so a source nested under
   the synthetic "Organic / Unknown" group is still treated as a channel, not a
   campaign — the group node just adds a display level above it. */
const CHILD_ROLE = { orggroup: 'channel', channel: 'campaign', campaign: 'adset', adset: 'creative', creative: 'creative' };

/* A node earns a row if it has attributed sign ups, OR if it carries real spend with none.
   That second case is the synthetic "Channel spend · campaign unattributed" node. It used
   to be shown at channel level only, which left a channel's SPEND exceeding the sum of its
   visible children with nothing on screen to explain the gap — e.g. Google reading ₹4,566
   while its only visible campaign read ₹3,337, the ₹1,229 difference being app-campaign
   spend we cannot attribute to any user. Money we spent is never hidden now: it gets a row
   wherever it lands, and the SPEND column reconciles down every level.
   Only the synthetic nodes are ever spend-without-signups (attachSpend pro-rates all other
   spend onto rows that DO have users), so this surfaces exactly one extra row per affected
   channel — not a wave of empty ad-level rows. */
const isVisibleNode = (m) => m.signups >= 1 || m.spend > 0;

export function buildRows(tree, mode, f, plat) {
  const rows = [];
  function walk(node, parentId, level, prefix, role, chKey) {
    const m = nodeMetrics(node, mode, f, plat);
    if (!isVisibleNode(m)) return;
    // Base "has children" on VISIBLE children only, so a node never renders a dead expand
    // chevron over rows that are about to be filtered out.
    const hasKids = !!(node.children || []).some((c) => isVisibleNode(nodeMetrics(c, mode, f, plat)));
    // Resolve the platform split up front for the same reason: a spend-only node has a leaf
    // but no sign ups in either platform, so it must not advertise children it cannot draw.
    const platRows = !hasKids && node.leaf && plat === 'all'
      ? PLATFORMS.map((p, i) => ({ p, i, pm: leafAgg(node.leaf, mode, f, p[0]) })).filter((x) => x.pm.signups >= 1)
      : [];
    // chKey is the CHANNEL this row descends from, carried down so the UI can label the
    // ad-set level correctly per channel — it holds real ad-set names on Meta but the raw
    // utm_medium ("ppc"/"cpc") everywhere else. See LVL_LABEL in index.js.
    const myKey = role === 'channel' ? (node.key || (node.cls || '').replace('d-', '') || null) : chKey;
    // `label` is presentation only — the ad platform's own name for a campaign the server
    // merged with differently-named spend. `name` stays the value members actually carry, so
    // every filter and link keeps working whether or not the server sends a label.
    rows.push({ id: prefix, parent: parentId, level, role, name: node.name, label: node.label, key: node.key, chKey: myKey, cls: node.cls || '', hasKids: hasKids || platRows.length > 0, m });
    if (hasKids) node.children.forEach((c, i) => walk(c, prefix, level + 1, prefix + '>' + i, CHILD_ROLE[role], myKey));
    else platRows.forEach(({ p, i, pm }) => {
      rows.push({ id: prefix + '>p' + i, parent: prefix, level: level + 1, role: 'platform', name: p[1], cls: p[2], chKey: myKey, kind: 'platform', hasKids: false, m: pm });
    });
  }
  tree.forEach((ch, i) => walk(ch, null, 0, 'n' + i, ch.kind === 'orggroup' ? 'orggroup' : 'channel', null));
  return rows;
}

/* Leaf creatives ranked for the "top creatives" table. */
export function collectLeaves(tree, mode, f, plat) {
  const leaves = [];
  function collect(node, chCls) {
    if (node.leaf) { const z = leafAgg(node.leaf, mode, f, plat); if (z.signups >= 1) leaves.push({ name: node.name, cls: chCls, m: z }); return; }
    (node.children || []).forEach((c) => collect(c, chCls));
  }
  tree.forEach((ch) => {
    if (ch.children) collect(ch, ch.cls);
    else if (ch.leaf) { const z = leafAgg(ch.leaf, mode, f, ch.cls); if (z.signups >= 1) leaves.push({ name: ch.name, cls: ch.cls, m: z }); }
  });
  leaves.sort((a, b) => b.m.subscribed - a.m.subscribed);
  return leaves;
}
